import type { AxiosInstance } from "axios";
import type { ServiceDescriptor } from "settings/account/components/TagsManager/types";
import { PRODUCT_IDS } from "settings/account/components/TagsManager/requests/productAdapters";
import {
  listNotificationResourceCatalogEntries,
  normalizeNotificationResourceType,
  type TagModule,
} from "settings/account/types/tag-resource-types";
import {
  listResourcesOfType,
  type ResourceListItem,
} from "./resourceListers";

/**
 * Per-product service catalog for notification group assignments. Each
 * `ServiceDescriptor.type` is the notifications API `resourceType` string
 * (same keys as `resourceListers.ts`).
 */
export interface NotificationGroupProductAdapter {
  productId: string;
  productLabel: string;
  getServices: () => ServiceDescriptor[];
  listResources: (
    api: AxiosInstance,
    tenantId: number,
    resourceType: string
  ) => Promise<ResourceListItem[]>;
}

function servicesForModule(module: TagModule): ServiceDescriptor[] {
  return listNotificationResourceCatalogEntries()
    .filter((row) => row.module === module)
    .map((row) => ({
      type: row.notificationResourceType,
      label: row.label,
      singularLabel: row.singularLabel,
    }));
}

const kontrolEdgeAdapter: NotificationGroupProductAdapter = {
  productId: PRODUCT_IDS.KontrolEdge,
  productLabel: "Kontrol Edge",
  getServices: () => servicesForModule("kontrol-edge"),
  listResources: (api, tid, rt) => listResourcesOfType(api, tid, rt),
};

const reflectorV2Adapter: NotificationGroupProductAdapter = {
  productId: PRODUCT_IDS.CtxReflectorV2,
  productLabel: "CTX Reflector V2",
  getServices: () => servicesForModule("reflector-v2"),
  listResources: (api, tid, rt) => listResourcesOfType(api, tid, rt),
};

/** v1 types appear in linked-tag projection; manual picker listers pending. */
const reflectorV1Adapter: NotificationGroupProductAdapter = {
  productId: PRODUCT_IDS.CtxReflector,
  productLabel: "CTX Reflector",
  getServices: () => servicesForModule("reflector-v1"),
  listResources: (api, tid, rt) => listResourcesOfType(api, tid, rt),
};

const ADAPTERS: Record<string, NotificationGroupProductAdapter> = {
  [PRODUCT_IDS.KontrolEdge]: kontrolEdgeAdapter,
  [PRODUCT_IDS.CtxReflectorV2]: reflectorV2Adapter,
  [PRODUCT_IDS.CtxReflector]: reflectorV1Adapter,
};

export function getNotificationGroupAdapterForProduct(
  productInternalName: string | null | undefined
): NotificationGroupProductAdapter {
  if (productInternalName && ADAPTERS[productInternalName]) {
    return ADAPTERS[productInternalName];
  }
  return kontrolEdgeAdapter;
}

export function listNotificationGroupProducts(): NotificationGroupProductAdapter[] {
  return [kontrolEdgeAdapter, reflectorV2Adapter, reflectorV1Adapter];
}

/** Single dropdown: every service across KE / Reflector (same `resourceType` keys as APIs). */
export interface MergedNotificationServiceOption {
  type: string;
  label: string;
  singularLabel: string;
  productLabel: string;
  productId: string;
}

const PRODUCT_ORDER: string[] = [
  PRODUCT_IDS.KontrolEdge,
  PRODUCT_IDS.CtxReflectorV2,
  PRODUCT_IDS.CtxReflector,
];

export function listMergedNotificationServices(
  allowedProductIds?: Set<string> | null
): MergedNotificationServiceOption[] {
  const out: MergedNotificationServiceOption[] = [];
  for (const adapter of listNotificationGroupProducts()) {
    if (allowedProductIds && !allowedProductIds.has(adapter.productId)) {
      continue;
    }
    for (const svc of adapter.getServices()) {
      out.push({
        type: svc.type,
        label: svc.label,
        singularLabel: svc.singularLabel,
        productLabel: adapter.productLabel,
        productId: adapter.productId,
      });
    }
  }
  out.sort((a, b) => {
    const ia = PRODUCT_ORDER.indexOf(a.productId);
    const ib = PRODUCT_ORDER.indexOf(b.productId);
    const ra = ia === -1 ? PRODUCT_ORDER.length : ia;
    const rb = ib === -1 ? PRODUCT_ORDER.length : ib;
    return ra - rb;
  });
  return out;
}

export interface NotificationGlobalServiceEntry {
  service: ServiceDescriptor;
  product: NotificationGroupProductAdapter;
}

const GLOBAL_SERVICE_CATALOG: Record<string, NotificationGlobalServiceEntry> =
  (() => {
    const catalog: Record<string, NotificationGlobalServiceEntry> = {};
    for (const adapter of listNotificationGroupProducts()) {
      for (const svc of adapter.getServices()) {
        catalog[svc.type] = { service: svc, product: adapter };
      }
    }
    return catalog;
  })();

export function lookupNotificationResourceType(
  resourceType: string
): NotificationGlobalServiceEntry | null {
  return (
    GLOBAL_SERVICE_CATALOG[normalizeNotificationResourceType(resourceType)] ??
    null
  );
}
